import i18n from "../../i18n";
/**
 * Finance service — calls the payment-service GraphQL endpoint for
 * FinanceEntry and FinanceSummary operations.
 *
 * Pattern: raw fetch, token passed as parameter (callers obtain it via
 * getAccessTokenSilently()), URL from env var.
 *
 * REMOVED: getFinanceSettings, saveFinanceOpeningBalance (concept dropped).
 * CHANGED: saveFinanceEntry now always sends startDate; endDate optional.
 */
import {
    FinanceEntry,
    FinanceEntryInput,
    FinanceSummary,
} from '../../model/finance/Finance';

const API_URL = process.env.REACT_APP_PAYMENT_SERVICE_BASE_URL || '';

async function gqlPost<T>(token: string, query: string): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query }),
    });
    if (!res.ok) {
        throw new Error(`Finance service HTTP ${res.status}`);
    }
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors
            .map((e: { message: string }) => e.message)
            .join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

const ENTRY_FIELDS = `
    id businessId type description amount periodicity active category startDate endDate
`;

/**
 * Fetch all FinanceEntry records for a business.
 * businessId is Number()-coerced before embedding (known ID-vs-Int issue).
 */
export async function getFinanceEntries(
    businessId: string,
    token: string
): Promise<FinanceEntry[]> {
    const id = Number(businessId);
    const data = await gqlPost<{ financeEntries: FinanceEntry[] }>(
        token,
        `query { financeEntries(businessId: ${id}) { ${ENTRY_FIELDS} } }`
    );
    return data.financeEntries;
}

/**
 * Fetch the P&L summary for a given year (all new fields per backend contract).
 */
export async function getFinanceSummary(
    businessId: string,
    year: number,
    token: string
): Promise<FinanceSummary> {
    const id = Number(businessId);
    const data = await gqlPost<{ financeSummary: FinanceSummary }>(
        token,
        `query {
            financeSummary(businessId: ${id}, year: ${year}) {
                year
                months {
                    month gross credits revenue taxes commissions salaries
                    expenses investments net accumulatedBalance
                }
                yearGross yearCredits yearRevenue
                yearTaxes yearCommissions yearSalaries yearExpenses yearInvestments yearNet
                endBalance projectedYearRevenue projectedYearNet
            }
        }`
    );
    return data.financeSummary;
}

/**
 * Create or update a FinanceEntry (upsert: pass id to update, omit to create).
 * startDate is always sent. endDate is sent only when present and non-null.
 */
export async function saveFinanceEntry(
    input: FinanceEntryInput,
    token: string
): Promise<FinanceEntry> {
    const id = Number(input.businessId);
    const idField = input.id ? `id: "${input.id}"` : '';
    const categoryField = input.category != null
        ? `category: "${input.category.replace(/"/g, '\\"')}"`
        : '';
    const endDateField = input.endDate
        ? `endDate: "${input.endDate}"`
        : '';
    const data = await gqlPost<{ saveFinanceEntry: FinanceEntry }>(
        token,
        `mutation {
            saveFinanceEntry(input: {
                ${idField}
                businessId: ${id}
                type: ${input.type}
                description: "${input.description.replace(/"/g, '\\"')}"
                amount: ${input.amount}
                periodicity: ${input.periodicity}
                active: ${input.active}
                ${categoryField}
                startDate: "${input.startDate}"
                ${endDateField}
            }) { ${ENTRY_FIELDS} }
        }`
    );
    return data.saveFinanceEntry;
}

/**
 * Delete a FinanceEntry by id.
 */
export async function deleteFinanceEntry(
    id: string,
    businessId: string,
    token: string
): Promise<boolean> {
    const bId = Number(businessId);
    const data = await gqlPost<{ deleteFinanceEntry: boolean }>(
        token,
        `mutation { deleteFinanceEntry(id: "${id}", businessId: ${bId}) }`
    );
    return data.deleteFinanceEntry;
}
